import {
    GTagGroup,
    GoogleAnalyticsEvent,
    GooglePageViewInput,
    GoogleAnalyticsEventData,
    GoogleItemInput,
    GoogleListInput,
    GoogleCheckoutInput,
} from "./types";

export function pageView(data: GooglePageViewInput): GoogleAnalyticsEvent {
    const { page_title, page_location, send_to = GTagGroup.Default } = data;
    return {
        event: "page_view",
        properties: {
            page_title,
            page_location,
        },
        send_to,
    };
}

export function viewItem(data: GoogleItemInput): GoogleAnalyticsEvent {
    const { currency, value, items, send_to = GTagGroup.Default } = data;
    return {
        event: "view_item",
        properties: {
            currency,
            value,
            items,
        },
        send_to,
    };
}

export function viewItemList(data: GoogleListInput): GoogleAnalyticsEvent {
    const { items, item_list_name, item_list_id, send_to = GTagGroup.Default } = data;
    return {
        event: "view_item_list",
        properties: {
            items,
            item_list_name,
            item_list_id,
        },
        send_to,
    };
}

export function addToWishlist(data: GoogleItemInput): GoogleAnalyticsEvent {
    const { currency, value, items, send_to = GTagGroup.Default } = data;
    return {
        event: "add_to_wishlist",
        properties: {
            currency,
            value,
            items,
        },
        send_to,
    };
}

export function addToCart(data: GoogleItemInput): GoogleAnalyticsEvent {
    const { currency, value, items, send_to = GTagGroup.Default } = data;
    return {
        event: "add_to_cart",
        properties: {
            currency,
            value,
            items,
        },
        send_to,
    };
}

export function viewCart(data: GoogleItemInput): GoogleAnalyticsEvent {
    const { currency, value, items, send_to = GTagGroup.Default } = data;
    return {
        event: "view_cart",
        properties: {
            currency,
            value,
            items,
        },
        send_to,
    };
}

export function removeFromCart(data: GoogleItemInput): GoogleAnalyticsEvent {
    const { currency, value, items, send_to = GTagGroup.Default } = data;
    return {
        event: "remove_from_cart",
        properties: {
            currency,
            value,
            items,
        },
        send_to,
    };
}

export function beginCheckout(data: GoogleCheckoutInput): GoogleAnalyticsEvent {
    const { currency, value, coupon, items, send_to = GTagGroup.Default } = data;
    return {
        event: "begin_checkout",
        properties: {
            currency,
            value,
            items,
            coupon,
        },
        send_to,
    };
}

export function addPaymentInfo(data: GoogleCheckoutInput): GoogleAnalyticsEvent {
    const { currency, value, coupon, payment_type, items, send_to = GTagGroup.Default } = data;
    return {
        event: "add_payment_info",
        properties: {
            currency,
            value,
            items,
            coupon,
            payment_type,
        },
        send_to,
    };
}

export function addShippingInfo(data: GoogleCheckoutInput): GoogleAnalyticsEvent {
    const { currency, value, coupon, shipping_tier, items, send_to = GTagGroup.Default } = data;
    return {
        event: "add_shipping_info",
        properties: {
            currency,
            value,
            items,
            coupon,
            shipping_tier,
        },
        send_to,
    };
}

export function purchase(data: GoogleCheckoutInput): GoogleAnalyticsEvent {
    const { currency, value, coupon, shipping, tax, transaction_id, items, send_to = GTagGroup.Default } = data;
    return {
        event: "purchase",
        properties: {
            currency,
            value,
            items,
            coupon,
            shipping,
            tax,
            transaction_id,
        },
        send_to,
    };
}

interface SelectContentData extends GoogleAnalyticsEventData {
    content_id: string;
    content_type: string;
    content_title?: string;
}

export function selectContent(data: SelectContentData): GoogleAnalyticsEvent {
    const { content_id, content_type, content_title, send_to = GTagGroup.Default } = data;
    return {
        event: "select_content",
        properties: {
            content_id,
            content_type,
            content_title,
        },
        send_to,
    };
}

// LS B2B
interface B2BEventData extends GoogleAnalyticsEventData {
    orderNumber?: string;
    buyerId?: string;
    userId?: string;
    brandId?: string;
    companyId?: string;
    orderId?: string;
}

export function rr(data: B2BEventData): GoogleAnalyticsEvent {
    const { event, orderNumber, buyerId, userId, brandId, companyId, orderId, send_to = GTagGroup.Default } = data;
    return {
        event,
        properties: {
            orderId,
            orderNumber,
            buyerId,
            brandId,
            companyId,
            userId,
        },
        send_to,
    };
}

export function draftOrderViewedByBuyer(data: B2BEventData): GoogleAnalyticsEvent {
    const { orderNumber, userId, companyId, send_to = GTagGroup.Default } = data;
    return {
        event: "draft-order-viewed-by-buyer",
        properties: {
            orderNumber,
            companyId,
            userId,
        },
        send_to,
    };
}

export function smartOrderDismissRecommendedProducts(data: B2BEventData): GoogleAnalyticsEvent {
    const { userId, companyId, brandId, send_to = GTagGroup.Default } = data;
    return {
        event: "smart-order-dismiss-recommended-products",
        properties: {
            brandId,
            companyId,
            userId,
        },
        send_to,
    };
}

export function smartOrderAddProductsToCart(data: B2BEventData): GoogleAnalyticsEvent {
    const { userId, companyId, brandId, send_to = GTagGroup.Default } = data;
    return {
        event: "smart-order-add-products-to-cart",
        properties: {
            brandId,
            companyId,
            userId,
        },
        send_to,
    };
}

export function replenishmentSummaryPageView(data: B2BEventData): GoogleAnalyticsEvent {
    const { userId, brandId, send_to = GTagGroup.Default } = data;
    return {
        event: "replenishment-summary-page-view",
        properties: {
            userId,
            brandId,
        },
        send_to,
    };
}

export function replenishmentOrderDetailsPageView(data: B2BEventData): GoogleAnalyticsEvent {
    const { userId, brandId, companyId, send_to = GTagGroup.Default } = data;
    return {
        event: "replenishment-order-details-page-view",
        properties: {
            userId,
            brandId,
            companyId,
        },
        send_to,
    };
}

export const marketPlace = (data: {
    event: string;
    me: any;
    scope: any;
    send_to?: GTagGroup;
    payload?: any;
}): GoogleAnalyticsEvent => ({
    event: data.event,
    properties: {
        userId: data.me?._id,
        brandId: data.scope?.brand?._id,
        emailDomain: data.me?.email?.split("@")[1],
        brandName: data.scope?.brand?.name,
        ...data.payload,
    },
    send_to: data.send_to || GTagGroup.Default,
});

export function backendBrandEvent(data: any): GoogleAnalyticsEvent {
    const { event = "", properties = {}, send_to = GTagGroup.Brand } = data;
    const payload = { ...properties, send_to };
    switch (event) {
        case "page_view":
            return pageView(payload);
        case "select_content":
            return selectContent(payload);
        case "view_cart":
            return viewCart(payload);
        case "begin_checkout":
            return beginCheckout(payload);
        case "add_shipping_info":
            return addShippingInfo(payload);
        case "add_payment_info":
            return addPaymentInfo(payload);
        case "purchase":
            return purchase(payload);
        default:
            return data;
    }
}
